import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Pencil, Search, Upload } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type CreatorBulkResult, type CreatorCsvResult, type CreatorDiscoveryRun, type CreatorPool } from '@/api/client'
import { CK, SKIP_REASON_LABEL, criteriaSummary, fmtCount, languageLabel, usePoolDiscovery, useCreatorMutation } from '@/lib/creators'
import { useTargets } from '@/lib/queries'
import { formatShort } from '@/lib/time'
import { Button, Field, FileButton, KeyValue, PlatformMark, Skeleton, StatusDot, Textarea, errorText, type KeyValueItem } from '@/ui'
import { Section } from '../campaigns/shared'

function Figure({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-meta text-muted-foreground">{label}</div>
      <div className="mt-1 truncate text-title font-semibold tabular-nums">{value}</div>
      {hint && <div className="mt-1 text-meta text-muted-foreground">{hint}</div>}
    </div>
  )
}

export const skippedTotal = (r: CreatorDiscoveryRun) => Object.values(r.skipped ?? {}).reduce((a, n) => a + (n ?? 0), 0)

/** "팔로워 조건 4 · 브랜드 2": why accounts were left out, in the console's words. */
export function skippedLine(r: CreatorDiscoveryRun): string {
  const sk = r.skipped ?? {}
  return Object.entries(SKIP_REASON_LABEL)
    .filter(([k]) => (sk[k as keyof typeof sk] ?? 0) > 0)
    .map(([k, label]) => `${label} ${sk[k as keyof typeof sk]}`)
    .join(' · ')
}

/** 찾기 — the run in progress or the last one, and 지금 찾기. */
export function DiscoveryCard({ pool }: { pool: CreatorPool }) {
  const qc = useQueryClient()
  const q = usePoolDiscovery(pool.id)
  const d = q.data
  const [problem, setProblem] = useState<string | null>(null)
  const start = useCreatorMutation(() => api.discoverPool(pool.id))
  const run = d?.last_run
  const running = !!d?.running

  // A run that just finished has added people: read the list and the counts again.
  const was = useRef(false)
  useEffect(() => {
    if (was.current && !running) void qc.invalidateQueries({ queryKey: CK.all })
    was.current = running
  }, [running, qc])

  const go = async () => {
    setProblem(null)
    try {
      await start.mutateAsync()
      await q.refetch()
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : errorText(e))
    }
  }

  return (
    <Section
      title="찾기"
      aside={pool.discovery_enabled ? '매일 09:00' : '자동은 꺼져 있다'}
      actions={
        <Button size="sm" variant="primary" icon={Search} loading={running || start.isPending} onClick={() => void go()}>
          {running ? '찾는 중' : '지금 찾기'}
        </Button>
      }
    >
      {!d ? (
        <Skeleton className="h-20" />
      ) : (
        <div className="space-y-3">
          {run ? (
            <div role="status" aria-label="찾기 진행" className="space-y-2">
              <p className="flex items-center gap-2 text-table">
                {run.status === 'running' ? (
                  <>
                    <StatusDot tone="publishing" pulse />
                    <b>찾는 중</b>
                  </>
                ) : run.status === 'failed' ? (
                  <>
                    <StatusDot tone="failed" />
                    <b>실패</b>
                  </>
                ) : (
                  <>
                    <StatusDot tone="published" />
                    <b>끝</b>
                  </>
                )}
                <span className="text-muted-foreground">
                  · {run.trigger === 'schedule' ? '자동' : '직접'} · {formatShort(run.finished_at ?? run.started_at)}
                </span>
              </p>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Figure label="검색" value={fmtCount(run.searched)} />
                <Figure label="확인" value={fmtCount(run.looked_up)} />
                <Figure label="추가" value={fmtCount(run.added)} />
                <Figure label="걸러냄" value={fmtCount(skippedTotal(run))} />
              </div>
              {skippedLine(run) && <p className="text-meta text-muted-foreground">걸러낸 이유: {skippedLine(run)}</p>}
              {run.status === 'failed' && run.error && <p className="text-table text-status-failed">{run.error}</p>}
            </div>
          ) : (
            <p className="text-table text-muted-foreground">아직 찾은 적 없다. 지금 찾기를 누르면 조건에 맞는 사람을 찾아 풀에 넣는다.</p>
          )}
          <p className="text-meta tabular-nums text-muted-foreground">
            오늘 조회 {d.today_lookups} / {d.daily_lookups}
            {d.candidates_pending > 0 && ` · 확인 대기 후보 ${fmtCount(d.candidates_pending)}`}
          </p>
          {!d.search_ready && pool.search_enabled && (
            <p className="text-meta text-muted-foreground">검색 키가 없어 씨앗 계정에서만 넓혀 간다.</p>
          )}
          {problem && (
            <p role="alert" className="text-table text-status-failed">
              {problem}
            </p>
          )}
        </div>
      )}
    </Section>
  )
}

/** 조건 — read only, 편집 opens the form. */
export function CriteriaCard({ pool, onEdit }: { pool: CreatorPool; onEdit: () => void }) {
  const targets = useTargets().data ?? []
  const account = targets.find((t) => t.id === pool.instagram_target_id)?.name
  const items: KeyValueItem[] = [
    {
      label: '플랫폼',
      value: <PlatformMark platform={pool.platform} withName />,
    },
    { label: '조건', value: criteriaSummary(pool) },
    { label: '언어', value: languageLabel(pool.language) },
    ...(pool.seed_handles.length > 0 ? [{ label: '씨앗', value: `${pool.seed_handles.length}개` }] : []),
    { label: '검색', value: pool.search_enabled ? '사용' : '쓰지 않음' },
    { label: '자동', value: pool.discovery_enabled ? `매일 · 하루 조회 ${pool.daily_lookups}` : '끔' },
    ...(pool.platform === 'instagram'
      ? [{ label: '조회 계정', value: account ?? <span className="text-status-publishing">지정 안 함</span> }]
      : []),
  ]
  return (
    <Section
      title="조건"
      actions={
        <Button size="sm" variant="ghost" icon={Pencil} aria-label="조건 편집" onClick={onEdit}>
          편집
        </Button>
      }
    >
      <KeyValue items={items} />
    </Section>
  )
}

/** The outcome of a paste or a CSV: the counts, then every line that was refused. */
export function AddResult({ r }: { r: CreatorBulkResult | CreatorCsvResult }) {
  const bad = 'invalid' in r ? r.invalid.map((x) => `${x.line} — ${x.error}`) : r.row_errors.map((x) => `${x.row}번째 줄 — ${x.message}`)
  return (
    <div role="status" className="space-y-2 rounded-md bg-muted/60 p-3 text-table">
      <p className="tabular-nums">
        추가 <b>{r.added}</b> · 이미 있음 <b>{r.existing}</b> · 연락 금지 <b>{r.suppressed}</b>
        {bad.length > 0 && (
          <>
            {' '}
            · 잘못된 줄 <b>{bad.length}</b>
          </>
        )}
      </p>
      {bad.length > 0 && (
        <ul aria-label="잘못된 줄" className="space-y-0.5 text-meta text-status-failed">
          {bad.map((line, i) => (
            <li key={i} className="break-words">
              {line}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Paste handles or profile addresses, or upload a CSV. */
export function AddPeople({ pool }: { pool: CreatorPool }) {
  const [text, setText] = useState('')
  const [result, setResult] = useState<CreatorBulkResult | CreatorCsvResult | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const bulk = useCreatorMutation((lines: string) => api.bulkAddPoolMembers(pool.id, lines))
  const csv = useCreatorMutation((body: string) => api.importPoolCsv(pool.id, body))

  const add = async () => {
    setProblem(null)
    try {
      setResult(await bulk.mutateAsync(text))
      setText('')
    } catch (e) {
      setProblem(errorText(e))
    }
  }

  return (
    <div className="space-y-3">
      <Field label="아이디나 프로필 주소를 한 줄에 하나씩" hint={`주소는 플랫폼을 읽고, 아이디만 적으면 이 풀의 플랫폼(${pool.platform === 'instagram' ? 'Instagram' : 'Bluesky'})으로 본다.`}>
        <Textarea
          rows={5}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="font-mono text-meta"
          placeholder={'@some_creator\nhttps://www.instagram.com/another_one/'}
        />
      </Field>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" loading={bulk.isPending} disabled={text.trim() === ''} onClick={() => void add()}>
          추가
        </Button>
        <FileButton
          icon={Upload}
          inputLabel="CSV 파일"
          accept=".csv,text/csv"
          onChange={async (e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (!file) return
            setProblem(null)
            try {
              setResult(await csv.mutateAsync(await file.text()))
            } catch (err) {
              setProblem(errorText(err))
            }
          }}
        >
          CSV로 올리기
        </FileButton>
      </div>
      <p className="text-meta text-muted-foreground">CSV는 첫 줄이 머리글이고 handle 열이 있어야 한다. platform, followers, display_name, bio는 있으면 쓴다.</p>
      {problem && (
        <p role="alert" className="text-table text-status-failed">
          {problem}
        </p>
      )}
      {result && <AddResult r={result} />}
    </div>
  )
}
